import React, { useEffect, useMemo, useState } from "react";
import { createSectionWorkType } from "../../../../api/projectProgressApi";
import "../AddWorkTypeModal/AddWorkTypeModal.css";
import "./AddMilestoneModal.css";

const normalizeName = (value) => String(value || "").trim().toLowerCase();

const PencilIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 20h4.5L19 9.5 14.5 5 4 15.5V20z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M12.5 7l4.5 4.5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const AddMilestoneModal = ({
  open,
  projectId,
  sectionId,
  existingNames = [],
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState("");
  const [planDate, setPlanDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const existingSet = useMemo(
    () => new Set(existingNames.map(normalizeName)),
    [existingNames],
  );

  useEffect(() => {
    if (!open) return;
    setName("");
    setPlanDate("");
    setSubmitting(false);
    setNameError("");
    setSubmitError("");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const trimmedName = name.trim();
  const isDuplicate =
    Boolean(trimmedName) && existingSet.has(normalizeName(trimmedName));
  const canSubmit =
    Boolean(trimmedName) && Boolean(planDate) && !isDuplicate && !submitting;

  const handleNameChange = (value) => {
    setName(value);
    setSubmitError("");
    const key = normalizeName(value);
    if (key && existingSet.has(key)) {
      setNameError("Такая веха уже есть в этом разделе!");
    } else {
      setNameError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trimmedName) {
      setNameError("Укажите название вехи");
      return;
    }
    if (existingSet.has(normalizeName(trimmedName))) {
      setNameError("Такая веха уже есть в этом разделе!");
      return;
    }
    if (!planDate) {
      setSubmitError("Укажите плановую дату");
      return;
    }
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      setSubmitError("");
      const created = await createSectionWorkType(projectId, sectionId, {
        name: trimmedName,
        is_milestone: true,
        unit_weight_percent: 0,
        plan_start_date: planDate,
        plan_end_date: planDate,
        completion_percent: 0,
      });
      onCreated?.(created);
      onClose();
    } catch (err) {
      setSubmitError(err?.message || "Не удалось добавить веху");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="add-work-type-modal-backdrop"
        onClick={() => !submitting && onClose()}
      />
      <div
        className="add-work-type-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-milestone-modal-title"
      >
        <h2
          id="add-milestone-modal-title"
          className="add-work-type-modal__title"
        >
          Добавление новой вехи
        </h2>

        <div className="add-work-type-modal__hint">
          <span className="add-work-type-modal__hint-icon" aria-hidden>
            ⚠
          </span>
          <p>Это контрольная точка без трудозатрат и удельного веса.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <label
            className="add-work-type-modal__label"
            htmlFor="add-milestone-name"
          >
            Название новой вехи
          </label>
          <div className="add-milestone-modal__field">
            <input
              id="add-milestone-name"
              type="text"
              className={`add-milestone-modal__input${
                nameError ? " add-milestone-modal__input--error" : ""
              }`}
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              disabled={submitting}
              placeholder="Введите название"
              autoFocus
            />
            <span className="add-milestone-modal__field-icon" aria-hidden>
              <PencilIcon />
            </span>
          </div>
          {nameError && (
            <p className="add-work-type-modal__error add-milestone-modal__error">
              <span aria-hidden>⚠</span>
              {nameError}
            </p>
          )}

          <label
            className="add-work-type-modal__label add-milestone-modal__label-spaced"
            htmlFor="add-milestone-plan-date"
          >
            Плановая дата
          </label>
          <div className="add-milestone-modal__field">
            <input
              id="add-milestone-plan-date"
              type="date"
              className="add-milestone-modal__input"
              value={planDate}
              onChange={(e) => {
                setPlanDate(e.target.value);
                setSubmitError("");
              }}
              disabled={submitting}
            />
          </div>

          {submitError && (
            <p className="add-work-type-modal__error">{submitError}</p>
          )}

          <div className="add-work-type-modal__actions">
            <button
              type="button"
              className="add-work-type-modal__btn add-work-type-modal__btn--cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="add-work-type-modal__btn add-work-type-modal__btn--submit"
              disabled={!canSubmit}
            >
              {submitting ? "Добавление..." : "Добавить"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddMilestoneModal;
